import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Pagination,
  Skeleton,
  useMediaQuery,
  useTheme,
  Fade,
  TableSortLabel,

  Divider,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { motion, AnimatePresence } from 'framer-motion';
import { getPayslips, getCompletedPayrolls } from '../../api/payroll';
import PayslipDialog from '../../components/PayslipDialog';
import { useAuth } from '../../context/AuthContext';

const normalizeAccessValue = (value) => String(value || '').trim().toLowerCase();

const normalizeAccessKey = (value) => normalizeAccessValue(value)
  .replace(/\bphilippines\b/g, 'phils')
  .replace(/\bcorporation\b/g, 'corp')
  .replace(/\bincorporated\b/g, 'inc')
  .replace(/[^a-z0-9]/g, '');

const accessMatches = (accessValues, candidate) => {
  const normalizedCandidate = normalizeAccessValue(candidate);
  if (!normalizedCandidate) return false;

  const candidateKey = normalizeAccessKey(candidate);
  return accessValues.some((accessValue) => {
    const accessKey = normalizeAccessKey(accessValue);
    return normalizedCandidate === accessValue ||
      normalizedCandidate.includes(accessValue) ||
      accessValue.includes(normalizedCandidate) ||
      (candidateKey && accessKey && candidateKey === accessKey);
  });
};

const Payslips = () => {
  // State management
  const [payslips, setPayslips] = useState([]);
  const [stores, setStores] = useState([]);
  const [datePeriods, setDatePeriods] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [datePeriodsLoading, setDatePeriodsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStore, setSelectedStore] = useState('');
  const [datePeriod, setDatePeriod] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false);
  const [selectedPayslipData, setSelectedPayslipData] = useState(null);

  // Enhanced table state
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('dateGenerated');
  const [dense, setDense] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);


  // Theme and responsive
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super-admin' || user?.role === 'admin';
  const groupAccessValues = useMemo(
    () => (Array.isArray(user?.groupAccess) ? user.groupAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );
  const storeAccessValues = useMemo(
    () => (Array.isArray(user?.storeAccess) ? user.storeAccess.map(normalizeAccessValue).filter(Boolean) : []),
    [user]
  );
  const companyAccessValues = useMemo(
    () => [user?.company].map(normalizeAccessValue).filter(Boolean),
    [user]
  );

  const userCanAccessStore = (store) => (
    isSuperAdmin ||
    accessMatches(storeAccessValues, store) ||
    accessMatches(groupAccessValues, store) ||
    accessMatches(companyAccessValues, store)
  );


  // Fetch payslips data
  const fetchPayslips = async (pageRequest=page, rows=rowsPerPage, search=searchTerm, newOrder=order, newOrderBy=orderBy) => {
    // Validate required fields
    if (!selectedStore) {
      enqueueSnackbar('Please select a store', { variant: 'warning' });
      return;
    }

    if (!stores.some((store) => store.value === selectedStore)) {
      enqueueSnackbar('Selected store is not accessible to your account', { variant: 'warning' });
      return;
    }

    if (!datePeriod) {
      enqueueSnackbar('Please select a date period', { variant: 'warning' });
      return;
    }

    setTableLoading(true);
    setError(null);
    try {
      let [start, end] = datePeriod.split(' - ');
      if (!start || !end) {
        enqueueSnackbar('Invalid date period format', { variant: 'error' });
        return;
      }
      // Add 8 hours to the start and end times
      start = new Date(new Date(start).setHours(0, 0, 0, 0) + 8 * 60 * 60 * 1000);
      end = new Date(new Date(end).setHours(0, 0, 0, 0) + 8 * 60 * 60 * 1000);

      const requestData = {
        page: pageRequest,
        limit: rows,
        store: selectedStore,
        startDate: new Date(start).toISOString(),
        endDate: new Date(end).toISOString(),
        status: 'completed',
        search: search,
        sortBy: newOrderBy,
        sortOrder: newOrder
      };

      const response = await getPayslips(requestData);

      if (response.status === 'success') {
        setPayslips(response.data);
        setTotalItems(response.pagination.totalItems);
        setDataLoaded(true);
      }
    } catch (err) {
      setError('Failed to fetch payslips data');
      console.error('Error fetching payslips:', err);
      const errorMessage = err.response?.data?.message || 'Failed to fetch payslips data';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch stores and date periods data from completed payrolls (combined API call)
  const fetchStoresAndDatePeriods = async () => {
    setStoresLoading(true);
    setDatePeriodsLoading(true);
    try {
      const response = await getCompletedPayrolls();

      if (response.status === 'success') {
        // Map stores to the expected format
        const storesData = (response.stores || []).filter(userCanAccessStore).map(store => ({
          value: store,
          label: store
        }));

        // Map date periods to the expected format
        const datePeriodsData = response.datePeriods.map(period => {
          const fromDate = new Date(period.payroll_from).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit'
          });
          const toDate = new Date(period.payroll_to).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit'
          });
          const formattedPeriod = `${fromDate} - ${toDate}`;

          return {
            value: formattedPeriod,
            label: formattedPeriod,
            originalFrom: period.payroll_from
          };
        });

        // Sort date periods chronologically (newest first)
        datePeriodsData.sort((a, b) => {
          return new Date(b.originalFrom) - new Date(a.originalFrom);
        });

        setStores(storesData);
        if (selectedStore && !storesData.some((store) => store.value === selectedStore)) {
          setSelectedStore('');
          setPayslips([]);
          setDataLoaded(false);
        }
        setDatePeriods(datePeriodsData);
      }
    } catch (err) {
      console.error('Error fetching stores and date periods from payrolls:', err);

    } finally {
      setStoresLoading(false);
      setDatePeriodsLoading(false);
    }
  };

  // Load stores and date periods on component mount
  useEffect(() => {
    fetchStoresAndDatePeriods();
  }, [user]);

  // Sorting utility functions
  const descendingComparator = (a, b, orderBy) => {
    try {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return bValue - aValue;
      }

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return bValue === aValue ? 0 : (bValue ? 1 : -1);
      }

      const aStr = String(aValue || '');
      const bStr = String(bValue || '');
      return bStr.localeCompare(aStr);
    } catch (error) {
      console.error('Error in descendingComparator:', error);
      return 0;
    }
  };


  // Calculate total pages from API data (server-side pagination)
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  // Use server-paginated data directly (no client-side pagination needed)
  const paginatedPayslips = payslips;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchPayslips();
      enqueueSnackbar('Data refreshed successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error refreshing payslips:', err);
      enqueueSnackbar('Failed to refresh data', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  // Enhanced table handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    // Trigger API call with new sorting
    fetchPayslips(page, rowsPerPage, searchTerm, newOrder, property);
  };

  // Event handlers

  const handleDatePeriodChange = (event) => {
    setDatePeriod(event.target.value);
    setPage(1);
  };

  // Handle search button click or Enter key press
  const handleSearch = () => {
    setPage(1);
    if (!searchTerm.trim()) {
      // Empty search should reset/clear search and show all results
      fetchPayslips(1, rowsPerPage, '', order, orderBy);
    } else {
      // Valid search term - perform search
      fetchPayslips(1, rowsPerPage, searchTerm.trim(), order, orderBy);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    // No longer triggers API call on every keystroke
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
    // Fetch data with new rows per page
    fetchPayslips(page, event.target.value, searchTerm, order, orderBy)
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Fetch data for the new page
    fetchPayslips(newPage, rowsPerPage, searchTerm, order, orderBy)
  };

  const handleGenerate = () => {
    fetchPayslips();
  };

  const handleViewPayslip = (payslip) => {
    
    // Try different possible field names
    const payrollId = payslip.payrollId || payslip.payroll_id;
    const employeeId = payslip.employeeId || payslip.employee_id;
    const employeeName = payslip.employeeName || payslip.employee_name;
    
    
    // Validate that we have the required IDs
    if (!payrollId || !employeeId) {
      console.error('Missing payrollId or employeeId:', {
        payrollId,
        employeeId,
        originalPayslip: payslip
      });
      enqueueSnackbar('Error: Missing payroll or employee information', { variant: 'error' });
      return;
    }
    
    setSelectedPayslipData({
      payrollId,
      employeeId,
      employeeName
    });
    setPayslipDialogOpen(true);
  };

  const handleClosePayslipDialog = () => {
    setPayslipDialogOpen(false);
    // Delay clearing the data to prevent rendering issues
    setTimeout(() => {
      setSelectedPayslipData(null);
    }, 100);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="200px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="300px" height={20} sx={{ mb: 3 }} />
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="rectangular" width="200px" height={36} />
            <Skeleton variant="rectangular" width="120px" height={36} />
          </Box>
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" width="100%" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  const handleExport = (format) => {
    if (payslips.length === 0) {
      alert('No data to export');
      return;
    }

    switch (format) {
      case 'CSV':
        exportToCSV();
        break;
      case 'Excel':
        exportToExcel();
        break;
      case 'PDF':
        exportToPDF();
        break;
      case 'Print':
        printTable();
        break;
      default:
        console.log(`Export format ${format} not implemented`);
    }
  };

  const exportToCSV = () => {
    const headers = ['Employee Name', 'Pay Period', 'Gross Pay', 'Net Pay'];
    const csvContent = [
      headers.join(','),
      ...payslips.map(payslip => [
        `"${payslip.employeeName}"`,
        `"${payslip.payPeriod}"`,
        Math.max(0, payslip.grossPay || 0),
        Math.max(0, payslip.netPay || 0)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payslips_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    // For Excel export, we'll create a CSV that can be opened in Excel
    // In a real application, you might want to use a library like xlsx
    const headers = ['Employee Name', 'Pay Period', 'Gross Pay', 'Net Pay'];
    const csvContent = [
      headers.join('\t'),
      ...payslips.map(payslip => [
        payslip.employeeName,
        payslip.payPeriod,
        Math.max(0, payslip.grossPay || 0),
        Math.max(0, payslip.netPay || 0)
      ].join('\t'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payslips_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    // For PDF export, we'll open a new window with a printable version
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payslips Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            h1 { color: #333; }
            .header { margin-bottom: 20px; }
            .date { color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Sparkle Payroll - Payslips Report</h1>
            <p class="date">Generated on: ${new Date().toLocaleDateString()}</p>
            <p>Total Records: ${payslips.length}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Pay Period</th>
                <th>Gross Pay</th>
                <th>Net Pay</th>
              </tr>
            </thead>
            <tbody>
              ${payslips.map(payslip => `
                <tr>
                  <td>${payslip.employeeName}</td>
                  <td>${payslip.payPeriod}</td>
                  <td>₱${Math.max(0, payslip.grossPay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>₱${Math.max(0, payslip.netPay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const printTable = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payslips Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            h1 { color: #333; }
            .header { margin-bottom: 20px; }
            .date { color: #666; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Sparkle Payroll - Payslips Report</h1>
            <p class="date">Generated on: ${new Date().toLocaleDateString()}</p>
            <p>Total Records: ${payslips.length}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Pay Period</th>
                <th>Gross Pay</th>
                <th>Net Pay</th>
              </tr>
            </thead>
            <tbody>
              ${payslips.map(payslip => `
                <tr>
                  <td>${payslip.employeeName}</td>
                  <td>${payslip.payPeriod}</td>
                  <td>₱${Math.max(0, payslip.grossPay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>₱${Math.max(0, payslip.netPay || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{ fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReceiptIcon sx={{ color: '#12C1DC' }} />
              Payslips
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  color: '#12C1DC',
                  '&:hover': { bgcolor: 'rgba(18, 193, 220, 0.1)' }
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: '#12C1DC' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/payroll">
              Payroll
            </Link>
            <Typography color="text.primary">Payslips</Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          </motion.div>
        )}

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Autocomplete
                  fullWidth
                  options={stores}
                  getOptionLabel={(option) => option.label || ''}
                  value={stores.find(store => store.value === selectedStore) || null}
                  onChange={(event, newValue) => {
                    setSelectedStore(newValue ? newValue.value : '');
                    setPage(1);
                    setDataLoaded(false);
                  }}
                  loading={storesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Store"
                      placeholder="Type to search stores..."
                    />
                  )}
                  noOptionsText="No stores found"
                  clearOnEscape
                  autoHighlight
                  disableClearable={false}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  fullWidth
                  options={datePeriods}
                  getOptionLabel={(option) => option.label || ''}
                  value={datePeriods.find(period => period.value === datePeriod) || null}
                  onChange={(event, newValue) => {
                    setDatePeriod(newValue ? newValue.value : '');
                    setPage(1);
                    setDataLoaded(false);
                  }}
                  loading={datePeriodsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Date Period"
                      placeholder="Select date period..."
                    />
                  )}
                  noOptionsText="No date periods found"
                  clearOnEscape
                  autoHighlight
                  disableClearable={false}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={handleGenerate}
                  disabled={tableLoading || !selectedStore || !datePeriod}
                  sx={{
                    height: '56px',
                    bgcolor: '#12C1DC',
                    '&:hover': { bgcolor: '#0FABDB' }
                  }}
                >
                  {tableLoading ? <CircularProgress size={24} /> : 'Generate'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Title and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 3,
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                component="h2"
                sx={{ fontWeight: 'bold', color: '#374151' }}
              >
                Payslips Data
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, maxWidth: { xs: '100%', md: '400px' } }}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search employee names, stores... (leave empty to show all)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchTerm('');
                            setPage(1);
                          }}
                          sx={{ color: '#9ca3af' }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#12C1DC' },
                      '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSearch}
                  sx={{
                    minWidth: 'auto',
                    px: 2,
                    bgcolor: '#12C1DC',
                    '&:hover': { bgcolor: '#0FABDB' }
                  }}
                >
                  {searchTerm.trim() ? 'Search' : 'Clear'}
                </Button>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Controls and Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Table Controls */}
            <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Show
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    entries
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-end' }
                }}>
                  <Tooltip title="Export CSV">
                    <IconButton 
                      size="small"
                      onClick={() => handleExport('CSV')}
                      sx={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 2, 
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <CsvIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export Excel">
                    <IconButton 
                      size="small"
                      onClick={() => handleExport('Excel')}
                      sx={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 2, 
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <ExcelIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export PDF">
                    <IconButton 
                      size="small"
                      onClick={() => handleExport('PDF')}
                      sx={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 2, 
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PdfIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print">
                    <IconButton 
                      size="small"
                      onClick={() => handleExport('Print')}
                      sx={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 2, 
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                      }}
                    >
                      <PrintIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Enhanced Table */}
            <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}>
              {tableLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: 1
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} sx={{ color: '#12C1DC' }} />
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        Loading payslips...
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              )}
              <Table
                stickyHeader
                size={dense ? 'small' : (isMobile ? "small" : "medium")}
                aria-label="enhanced payslips table"
              >
                <TableHead>
                  <TableRow sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell sx={{ 
                      fontWeight: 600, 
                      bgcolor: '#f9fafb',
                      color: '#374151',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Payslip
                    </TableCell>
                    <TableCell 
                      sortDirection={orderBy === 'employeeName' ? order : false}
                      sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'employeeName'}
                        direction={orderBy === 'employeeName' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'employeeName')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell 
                      sortDirection={orderBy === 'grossPay' ? order : false}
                      sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'grossPay'}
                        direction={orderBy === 'grossPay' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'grossPay')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Gross Pay
                      </TableSortLabel>
                    </TableCell>
                    <TableCell 
                      sortDirection={orderBy === 'netPay' ? order : false}
                      sx={{ 
                        fontWeight: 600, 
                        bgcolor: '#f9fafb',
                        color: '#374151',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === 'netPay'}
                        direction={orderBy === 'netPay' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'netPay')}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: '#12C1DC !important',
                          },
                        }}
                      >
                        Net Pay
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!dataLoaded && payslips.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                          No Data Loaded
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                          Please select a store and date period, then click "Generate" to load payslips data.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <AnimatePresence>
                      {paginatedPayslips.map((payslip, index) => {
                        const isEvenRow = index % 2 === 0;
                        return (
                          <motion.tr
                            key={payslip.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            style={{
                              display: 'table-row',
                              backgroundColor: isEvenRow ? '#ffffff' : '#f8f9fa'
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: isEvenRow ? '#f0f0f0 !important' : '#e9ecef !important'
                              },
                              '& > .MuiTableCell-root': {
                                borderRight: '1px solid #e0e0e0',
                                borderBottom: '1px solid #e0e0e0'
                              },
                              '& > .MuiTableCell-root:last-child': {
                                borderRight: 'none'
                              }
                            }}
                          >
                        <TableCell>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<GetAppIcon />}
                            onClick={() => handleViewPayslip(payslip)}
                            sx={{
                              bgcolor: '#ef4444',
                              '&:hover': { bgcolor: '#dc2626' },
                              textTransform: 'none',
                              borderRadius: 2
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#12C1DC', fontSize: '1.2rem' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {payslip.employeeName}
                              </Typography>
                              {isMobile && (
                                <Typography variant="caption" color="text.secondary">
                                  {payslip.store}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/*<MoneyIcon sx={{ color: '#6b7280', fontSize: '1rem' }} />*/}
                            <Typography variant="body2" fontWeight={500}>
                              ₱{Math.max(0, payslip.grossPay || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/*<MoneyIcon sx={{ color: '#10b981', fontSize: '1rem' }} />*/}
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#10b981' }}>
                              ₱{Math.max(0, payslip.netPay || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </Typography>
                          </Box>
                        </TableCell>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {dataLoaded && totalPages > 1 && (
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                gap: { xs: 2, sm: 0 }
              }}>
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, totalItems)} of {totalItems} entries
                  {searchTerm && ` (filtered from ${totalItems} total entries)`}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '8px',
                      '&.Mui-selected': {
                        backgroundColor: '#12C1DC',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#0FABDB'
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}

          </Paper>
        </motion.div>

        {/* Payslip Dialog */}
        {payslipDialogOpen && selectedPayslipData && (
          <PayslipDialog
            open={payslipDialogOpen}
            onClose={handleClosePayslipDialog}
            payrollId={selectedPayslipData.payrollId}
            employeeId={selectedPayslipData.employeeId}
            employeeName={selectedPayslipData.employeeName}
          />
        )}
      </Box>
    </Fade>
  );
};

export default Payslips;
